import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { UserManagementService } from "../../../../core/services/user-management.service";
import { forkJoin, map, Observable } from "rxjs";
import { ProgramDimensionDto } from "../../../../core/models/program/programDimensionDto";
import { AddProgramDto } from "../../../../core/models/program/addProgramDto";
import { DropDownDto } from "../../../../core/models/program/dropdownDto";
import { ProgramBCDto } from "../../../../core/models/program/programBCListDto";
import { ValidateLogin } from "../../../../core/models/validate-login.dto";
import { LoaderService } from "../../../../core/services/loader.service";
import { ProgramsService } from "../../../../core/services/programs.service";
import { UserRole } from "../../../../shared/enums/userRoles.enum";
import { CommonModule } from "@angular/common";
import { ClientListComponent } from "../client-list/client-list.component";
import { ProgramHeaderComponent } from "../program-header/program-header.component";
import { ModalStackerComponent } from "../../../../shared/components/modal-stacker/modal-stacker.component";
import { JniToastrService } from "../../../../core/services/jni-toastr.service";
import { ResultForkJoinDto } from "../../../../core/models/program/resultForkJoinDto";
import { SpinnerComponent } from "../../../../shared/components/spinner/spinner.component";
import { RadioButtonModule } from "primeng/radiobutton";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { InputTextModule } from "primeng/inputtext";
import { ClientDetailsComponent } from "../../../clients/components/client-details/client-details.component";

@UntilDestroy()
@Component({
  selector: "jni-program-summary",
  templateUrl: "./program-summary.component.html",
  styleUrls: ["./program-summary.component.scss"],
  imports: [
    CommonModule,
    ClientListComponent,
    ProgramHeaderComponent,
    ModalStackerComponent,
    SpinnerComponent,
    RadioButtonModule,
    ButtonModule,
    CardModule,
    InputTextModule,
    ClientDetailsComponent,
  ],
})
export class ProgramSummaryComponent implements OnInit {
  isReadOnly = true;
  showSmallLoader = false;
  programForm!: FormGroup;
  customerAccount = "";
  facilitatorList: DropDownDto[] = [];
  threePlList: DropDownDto[] = [];
  platfromList: DropDownDto[] = [];
  bcList: ProgramBCDto[] = [];
  programData = {} as AddProgramDto;
  programDimensions: ProgramDimensionDto[] = [];
  validateLogin = {} as ValidateLogin;
  programName = "";
  programStatus = "";
  programCreatedDate = "";
  isAdminCheck = false;
  isClientLockCheck = false;
  editHeaderStatus = false;
  result$!: Observable<ResultForkJoinDto>;
  isEditDimension = false;
  clientSummaryAccount = "";
  displayClientSummaryModal = false;

  @Input() programId = 0;
  @Output() closeModalEvent = new EventEmitter();

  constructor(
    private _programService: ProgramsService,
    private _toastr: JniToastrService,
    private _loaderService: LoaderService,
    private _fb: FormBuilder,
    private _userService: UserManagementService,
    private router: ActivatedRoute
  ) {
    this.router.params.subscribe(params => {
      if (params["id"] && params["id"] != 0) {
        this.programId = params["id"];
      }
    });
    this.initializeForm();
  }

  ngOnInit() {
    this.getAllSummaryValues();
  }

  getAllSummaryValues() {
    this._loaderService.setLoaderState(true);
    this.result$ = forkJoin({
      getProgramDetails: this._programService.getProgarmDetailsById(
        this.programId
      ),
      getFacilitatorList: this._programService.getFacilitatorList(),
      getThreePLList: this._programService.getThreePLList(),
      getPlatformList: this._programService.getPlatformList(),
      getWhoISupport: this._programService.getWhoISupport(),
      getProgramDimensions: this._programService.getProgramDimensions(),
      validateLogin: this._userService.validateLogin(),
    }).pipe(
      untilDestroyed(this),
      map(res => {
        return {
          whoISupport: res.getWhoISupport.data,
          programDimensions: res.getProgramDimensions.data,
          facilitatorList: res.getFacilitatorList.data,
          threePLList: res.getThreePLList.data,
          platformList: res.getPlatformList.data,
          validateLogin: res.validateLogin.data,
          programDetails: res.getProgramDetails.data,
        } as unknown as ResultForkJoinDto;
      })
    );

    this.result$.subscribe({
      next: (res: ResultForkJoinDto) => {
        const uniqueSupport = res.whoISupport.filter(
          (item: ProgramBCDto, index: number, self: ProgramBCDto[]) =>
            index ===
            self.findIndex(t => t.personnelNumber === item.personnelNumber)
        );
        this.bcList = uniqueSupport.map((item: ProgramBCDto) => ({
          ...item,
          displayValue: `${item.personnelNumber} : ${item.projectName}`,
        }));
        this.facilitatorList = res.facilitatorList;
        this.threePlList = res.threePLList;
        this.programDimensions = res.programDimensions;
        this.platfromList = res.platformList;
        this.validateLogin = res.validateLogin;
        if (res.programDetails) {
          this.programData = res.programDetails;
          if (
            this.programData.isClientLock &&
            this.validateLogin.userRoleId !== UserRole.Admin
          ) {
            this.isClientLockCheck = true;
          } else {
            this.isClientLockCheck = false;
          }
          this.programName = this.programData.name;
          this.programStatus = this.programData.programStatus;
          this.programCreatedDate = this.programData.createdDate;
        }
        this.checkIsAdmin();
        this._loaderService.setLoaderState(false);
      },
      error: (res: { message: string }) => {
        this._loaderService.setLoaderState(false);
        this._toastr.error("Error", res.message || "Data not found");
      },
    });
  }

  private initializeForm() {
    this.programForm = this._fb.group({
      id: [""],
      name: ["", Validators.required],
      brandingConsultantD365: ["", Validators.required],
      office: [""],
      description: [""],
      ecomOnboard: [false],
      inStoreCC: [false],
      usesWebsite: [false],
      clienttOwnInv: [false],
      nadelOwnInv: [false],
      intl: [false],
      website1: [""],
      website2: [""],
      dimensionD365: [""],
      uses3PL: [false],
      micro: [false],
      threePL1Id: [0],
      threePLOther: [""],
      avaTaxIntegration: [false],
      webPlatId: [0],
      inStoreCCProvider: [0],
      isApproved: [false],
      isDisabled: [false],
      isClientLock: [false],
    });
  }

  getCustomerAccount($event: string) {
    this.customerAccount = $event;
  }

  getSmallLoaderStatus($event: boolean) {
    this.showSmallLoader = $event;
  }

  onProgramEdit() {
    this.isReadOnly = false;
    this.programForm.enable();
    if (this.isAdminCheck) {
      this.isEditDimension = true;
    } else {
      this.isEditDimension = false;
    }
  }

  onProgramUpdate() {
    this._loaderService.setLoaderState(true);
    const formData = this.programForm.getRawValue() as AddProgramDto;
    this.programFieldMapper(formData);
    this._programService
      .UpdateProgram(this.programData)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          if (res.data != null) {
            this._toastr.success("Success", "Program edit successfully");
            this.programData = res.data;
            if (
              this.programData.isClientLock &&
              this.validateLogin.userRoleId !== UserRole.Admin
            ) {
              this.isClientLockCheck = true;
            } else {
              this.isClientLockCheck = false;
            }
            this.programStatus = this.programData.programStatus;
            this.programName = this.programData.name;
            this.isReadOnly = true;
            this.isEditDimension = false;
          } else {
            this._toastr.error("Error", res.message ?? "Error adding program");
          }
          this._loaderService.setLoaderState(false);
        },
        error: res => {
          this._loaderService.setLoaderState(false);
          this._toastr.error("Error", res.message ?? "Error adding program");
        },
      });
  }

  onCancel() {
    this.isReadOnly = true;
    this.programForm.disable();
    this.isEditDimension = false;
  }

  programFieldMapper(data: AddProgramDto) {
    this.programData.name = data.name;
    this.programData.dimensionD365 = data.dimensionD365;
    this.programData.office = data.office;
    this.programData.description = data.description;
    this.programData.brandingConsultantD365 = data.brandingConsultantD365;
    this.programData.nadelOwnInv = data.nadelOwnInv;
    this.programData.clientOwnInv = data.clientOwnInv;
    this.programData.uses3PL = data.uses3PL;
    this.programData.threePL1Id = data.threePL1Id ?? 0;
    this.programData.usesWebsite = data.usesWebsite;
    this.programData.webPlatId = data.webPlatId ?? 0;
    this.programData.threePLOther = data.threePLOther;
    this.programData.inStoreCC = data.inStoreCC;
    this.programData.inStoreCCProvider = data.inStoreCCProvider ?? 0;
    this.programData.intl = data.intl;
    this.programData.micro = data.micro;
    this.programData.ecomOnboard = data.ecomOnboard;
    this.programData.website1 = data.website1;
    this.programData.website2 = data.website2;
    this.programData.isApproved = data.isApproved;
    this.programData.isDisabled = data.isDisabled;
    this.programData.isClientLock = data.isClientLock;
    this.programData.avaTaxIntegration = data.avaTaxIntegration;
    this.programData.createdDate = "";
  }

  onApprove(isApproved: boolean) {
    this.showSmallLoader = true;
    this._programService
      .ProgramApprove(this.programId, isApproved)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: res => {
          this.showSmallLoader = false;
          if (res.data) {
            this.programData = res.data;
            this.programStatus = res.data.programStatus;
          }
        },
        error: res => {
          this.showSmallLoader = false;
          this._toastr.error("Error", res.message ?? "Data not found");
        },
      });
  }

  checkIsAdmin() {
    if (this.validateLogin.userRoleId == UserRole.Admin) {
      this.isAdminCheck = true;
    } else {
      this.isAdminCheck = false;
    }
  }

  openClientSummaryModal(event: string) {
    if (event) {
      this.clientSummaryAccount = event;
      this.displayClientSummaryModal = true;
    }
  }

  closeClientSummaryModal() {
    this.clientSummaryAccount = "";
    this.displayClientSummaryModal = false;
  }

  //addScollHiddenClass() {
  // if (this.displayClientSummaryModal) {
  //   this.renderer.addClass(document.body, 'p-overflow-hidden');
  // } else {
  //   this.renderer.removeClass(document.body, 'p-overflow-hidden');
  // }
  //}

  onCloseModal() {
    this.closeModalEvent.emit();
  }
}
